import type {
  AgentDetailDto,
  EndpointDto,
  Role,
  SdkOption,
  UpdateEndpointRequest,
} from '@agent-platform/shared';
import { hasRole, SDK_OPTIONS } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { EndpointExamples } from '@/components/agent-code';
import { expectedHeaders } from '@/components/agent-headers';
import { CopyField } from '@/components/copy-field';
import { ErrorAlert } from '@/components/error-alert';
import { MappingEditor } from '@/components/mapping-editor';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';

function Settings({
  endpoint,
  canEdit,
  onSave,
  saving,
  error,
}: {
  endpoint: EndpointDto;
  canEdit: boolean;
  onSave: (changes: UpdateEndpointRequest) => void;
  saving: boolean;
  error: unknown;
}) {
  const [perKey, setPerKey] = useState(String(endpoint.rateLimit.perKeyPerMinute));
  const [perAgent, setPerAgent] = useState(String(endpoint.rateLimit.perAgentPerMinute));
  const [origins, setOrigins] = useState(endpoint.allowedOrigins.join('\n'));
  const [overrides, setOverrides] = useState<SdkOption[]>(endpoint.allowedSdkOverrides);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Settings</CardTitle>
        <CardDescription>
          Rate limits, browser access (CORS) and which model options a mapping may set.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <ErrorAlert error={error} />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="rl-key">Requests per key per minute</Label>
            <Input
              id="rl-key"
              type="number"
              value={perKey}
              disabled={!canEdit}
              onChange={(e) => setPerKey(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="rl-agent">Requests per minute for the whole agent</Label>
            <Input
              id="rl-agent"
              type="number"
              value={perAgent}
              disabled={!canEdit}
              onChange={(e) => setPerAgent(e.target.value)}
            />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="origins">Allowed browser origins (one per line)</Label>
          <Textarea
            id="origins"
            className="font-mono text-xs"
            placeholder="https://app.example.com"
            value={origins}
            disabled={!canEdit}
            onChange={(e) => setOrigins(e.target.value)}
          />
          <p className="text-muted-foreground text-xs">
            Leave empty to block browser calls. Never put API keys in public web pages.
          </p>
        </div>
        <fieldset className="grid gap-1.5">
          <legend className="mb-1 text-sm font-medium">Model options a mapping may override</legend>
          <div className="flex flex-wrap gap-4">
            {SDK_OPTIONS.map((o) => (
              <label key={o} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={overrides.includes(o)}
                  onChange={(e) =>
                    setOverrides((cur) =>
                      e.target.checked ? [...cur, o] : cur.filter((x) => x !== o),
                    )
                  }
                />
                {o}
              </label>
            ))}
          </div>
        </fieldset>
        {canEdit && (
          <Button
            className="w-fit"
            disabled={saving}
            onClick={() =>
              onSave({
                rateLimit: { perKeyPerMinute: Number(perKey), perAgentPerMinute: Number(perAgent) },
                allowedOrigins: origins
                  .split('\n')
                  .map((o) => o.trim())
                  .filter(Boolean),
                allowedSdkOverrides: overrides,
              })
            }
          >
            Save settings
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ConversationHistory({
  endpoint,
  canEdit,
  onSave,
  saving,
}: {
  endpoint: EndpointDto;
  canEdit: boolean;
  onSave: (changes: UpdateEndpointRequest) => void;
  saving: boolean;
}) {
  const current = endpoint.conversationHistory;
  const [enabled, setEnabled] = useState(current.enabled);
  const [userHeader, setUserHeader] = useState(current.userHeader);
  const [scopeHeader, setScopeHeader] = useState(current.scopeHeader ?? '');
  const [maxMessages, setMaxMessages] = useState(String(current.maxMessages));
  const [maxTokens, setMaxTokens] = useState(String(current.maxTokens));
  const [autoTitle, setAutoTitle] = useState(current.autoTitle);
  const [summarize, setSummarize] = useState(current.summarize);
  const [summaryMaxTokens, setSummaryMaxTokens] = useState(String(current.summaryMaxTokens));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Conversation history</CardTitle>
        <CardDescription>
          Store each user&apos;s conversations and send recent history to the agent with every
          request.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Keep conversation history
        </label>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="ch-user">User header</Label>
            <Input
              id="ch-user"
              className="font-mono text-xs"
              value={userHeader}
              disabled={!canEdit}
              onChange={(e) => setUserHeader(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ch-scope">Scope header</Label>
            <Input
              id="ch-scope"
              className="font-mono text-xs"
              placeholder="None"
              value={scopeHeader}
              disabled={!canEdit}
              onChange={(e) => setScopeHeader(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">Leave empty for no scopes.</p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ch-messages">Messages sent to the model</Label>
            <Input
              id="ch-messages"
              type="number"
              min={1}
              max={200}
              value={maxMessages}
              disabled={!canEdit}
              onChange={(e) => setMaxMessages(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ch-tokens">Approximate history tokens</Label>
            <Input
              id="ch-tokens"
              type="number"
              min={1000}
              max={200000}
              value={maxTokens}
              disabled={!canEdit}
              onChange={(e) => setMaxTokens(e.target.value)}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={autoTitle}
            onChange={(e) => setAutoTitle(e.target.checked)}
          />
          Title new conversations automatically
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={summarize}
            onChange={(e) => setSummarize(e.target.checked)}
          />
          Summarise older messages
        </label>
        <p className="text-muted-foreground -mt-2 text-xs">
          Messages that fall out of the history are folded into a short running summary, written in
          the background by the agent&apos;s model, and sent before the recent messages.
        </p>
        {summarize && (
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="ch-summary-tokens">Summary size (tokens)</Label>
            <Input
              id="ch-summary-tokens"
              type="number"
              min={200}
              max={4000}
              value={summaryMaxTokens}
              disabled={!canEdit}
              onChange={(e) => setSummaryMaxTokens(e.target.value)}
            />
          </div>
        )}
        <p className="text-muted-foreground text-xs">
          The caller&apos;s backend sends the user header; anyone with the API key can read any
          user&apos;s history.
        </p>
        {canEdit && (
          <Button
            className="w-fit"
            disabled={saving}
            onClick={() =>
              onSave({
                conversationHistory: {
                  enabled,
                  userHeader: userHeader.trim().toLowerCase(),
                  scopeHeader: scopeHeader.trim().toLowerCase() || null,
                  maxMessages: Number(maxMessages),
                  maxTokens: Number(maxTokens),
                  autoTitle,
                  summarize,
                  summaryMaxTokens: Number(summaryMaxTokens),
                },
              })
            }
          >
            Save conversation history
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function EndpointDetailContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const { endpointId } = useParams({ from: '/app/endpoints/$endpointId' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = hasRole(role, 'admin');
  const [deleting, setDeleting] = useState(false);
  const endpoint = useQuery({
    queryKey: ['endpoint', tenantId, endpointId],
    queryFn: () => api<EndpointDto>(`/endpoints/${endpointId}`),
  });
  const agent = useQuery({
    queryKey: ['agent', tenantId, endpoint.data?.agentId],
    queryFn: () => api<AgentDetailDto>(`/agents/${endpoint.data!.agentId}`),
    enabled: !!endpoint.data,
  });
  const update = useMutation({
    mutationFn: (changes: UpdateEndpointRequest) =>
      api<EndpointDto>(`/endpoints/${endpointId}`, { method: 'PATCH', body: changes }),
    onSuccess: (updated) => {
      toast.success('Endpoint updated');
      queryClient.setQueryData(['endpoint', tenantId, endpointId], updated);
      void queryClient.invalidateQueries({ queryKey: ['endpoints', tenantId] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/endpoints/${endpointId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Endpoint deleted');
      void queryClient.invalidateQueries({ queryKey: ['endpoints', tenantId] });
      void navigate({ to: '/endpoints' });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!endpoint.data) return <ErrorAlert error={endpoint.error} />;
  const e = endpoint.data;
  const live = agent.data ? (agent.data.published ?? agent.data.draft) : null;

  return (
    <>
      <PageHeader
        title={e.agentName}
        description="Runs the agent’s published version for clients with an API key."
        actions={
          <>
            <Badge variant={e.enabled ? 'secondary' : 'outline'}>
              {e.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Button variant="outline" asChild>
              <Link to="/agents/$agentId" params={{ agentId: e.agentId }}>
                Edit agent
              </Link>
            </Button>
            {canEdit && (
              <>
                <Button variant="outline" onClick={() => update.mutate({ enabled: !e.enabled })}>
                  {e.enabled ? 'Disable' : 'Enable'}
                </Button>
                <Button variant="destructive" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              </>
            )}
          </>
        }
      />
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="mapping">Mapping</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Call this agent</CardTitle>
              <CardDescription>
                Send an API key from the{' '}
                <Link to="/api-keys" className="underline">
                  API keys
                </Link>{' '}
                page as a Bearer token or in <code>x-api-key</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <CopyField value={e.url} label="Endpoint URL" />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={e.streamingEnabled}
                  disabled={!canEdit}
                  onChange={(ev) => update.mutate({ streamingEnabled: ev.target.checked })}
                />
                Allow streaming responses (Server-Sent Events)
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={e.attachmentsEnabled}
                  disabled={!canEdit}
                  onChange={(ev) => update.mutate({ attachmentsEnabled: ev.target.checked })}
                />
                <span className="grid gap-0.5">
                  Allow file and image attachments
                  <span className="text-muted-foreground text-xs">
                    Callers may upload files to <code>…/files</code> and attach them to user
                    messages: images (PNG, JPEG, GIF, WebP), PDF, text, Markdown, CSV and JSON. When
                    off, both answer 400.
                  </span>
                </span>
              </label>
              <EndpointExamples
                endpoint={e}
                expected={expectedHeaders(e)}
                stateful={live?.memoryMode === 'stateful'}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="mapping">
          <MappingEditor
            key={`${e.id}:${e.mappingVersion}`}
            endpoint={e}
            tenantId={tenantId}
            canEdit={canEdit}
          />
        </TabsContent>
        <TabsContent value="settings" className="grid gap-4">
          <Settings
            key={e.updatedAt}
            endpoint={e}
            canEdit={canEdit}
            onSave={(c) => update.mutate(c)}
            saving={update.isPending}
            error={update.error}
          />
          <ConversationHistory
            key={`history:${e.updatedAt}`}
            endpoint={e}
            canEdit={canEdit}
            onSave={(c) => update.mutate(c)}
            saving={update.isPending}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleting} onOpenChange={setDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this endpoint?</AlertDialogTitle>
            <AlertDialogDescription>
              Its URL stops working immediately. Creating a new endpoint gives the agent a new URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="danger" onClick={() => remove.mutate()}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function EndpointDetailPage() {
  return (
    <RequireTenant>
      {(tenant) => <EndpointDetailContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
